/**
 * Helpers for `slide.background`, which is a CSS value that may be a solid
 * `#hex`, a `linear-gradient(...)`, or a full-bleed image
 * (`<color> url("…") center / cover no-repeat`). The classroom applies it via
 * CSS `background` (the shorthand accepts all three) — see
 * ELECTRON_BLUEPRINT.md §13.
 */

export type SlideBackgroundKind = 'solid' | 'gradient' | 'image';

export interface SlideLinearGradient {
  /** Degrees (CSS `linear-gradient(<angle>deg, ...)`). */
  angle: number;
  from: string;
  to: string;
}

export const DEFAULT_SOLID = '#ffffff';
export const DEFAULT_GRADIENT: SlideLinearGradient = { angle: 135, from: '#6366f1', to: '#22d3ee' };

export const SLIDE_GRADIENT_PRESETS: { label: string; gradient: SlideLinearGradient }[] = [
  { label: 'Indigo → Cyan', gradient: { angle: 135, from: '#6366f1', to: '#22d3ee' } },
  { label: 'Sunset', gradient: { angle: 135, from: '#f97316', to: '#db2777' } },
  { label: 'Forest', gradient: { angle: 135, from: '#16a34a', to: '#65a30d' } },
  { label: 'Slate', gradient: { angle: 180, from: '#334155', to: '#0f172a' } },
  { label: 'Dawn', gradient: { angle: 135, from: '#fde68a', to: '#fca5a5' } },
];

export function getSlideBackgroundKind(bg?: string): SlideBackgroundKind {
  if (typeof bg !== 'string') return 'solid';
  const v = bg.trim().toLowerCase();
  if (/url\(/.test(v)) return 'image';
  return v.startsWith('linear-gradient') ? 'gradient' : 'solid';
}

/** Clamp/normalise a hex color; returns `fallback` when unparseable. */
export function normalizeHexColor(input: string | undefined, fallback = DEFAULT_SOLID): string {
  if (!input) return fallback;
  const v = input.trim();
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v) ? v : fallback;
}

export function getSolidBackground(bg?: string): string {
  return getSlideBackgroundKind(bg) === 'solid' ? normalizeHexColor(bg) : DEFAULT_SOLID;
}

export function buildLinearGradient(g: SlideLinearGradient): string {
  return `linear-gradient(${g.angle}deg, ${g.from}, ${g.to})`;
}

/** Parse `linear-gradient(135deg, #aaa, #bbb)` back into its parts. */
export function parseLinearGradient(css?: string): SlideLinearGradient {
  if (!css || getSlideBackgroundKind(css) !== 'gradient') return { ...DEFAULT_GRADIENT };
  const inner = css.slice(css.indexOf('(') + 1, css.lastIndexOf(')'));
  // Split on commas that are NOT inside parens (e.g. rgb(...) stops).
  const parts = inner.split(/,(?![^(]*\))/).map((p) => p.trim());
  let angle = DEFAULT_GRADIENT.angle;
  let stops = parts;
  if (parts[0] && /deg\s*$/.test(parts[0])) {
    angle = parseFloat(parts[0]) || DEFAULT_GRADIENT.angle;
    stops = parts.slice(1);
  }
  const from = stops[0]?.split(/\s+/)[0] || DEFAULT_GRADIENT.from;
  const to = stops[stops.length - 1]?.split(/\s+/)[0] || DEFAULT_GRADIENT.to;
  return { angle, from, to };
}

export function getGradientBackground(bg?: string): SlideLinearGradient {
  return parseLinearGradient(bg);
}

// ---------- image backgrounds ----------

export interface SlideImageBackground {
  /** Public hosted image URL. */
  url: string;
  /** cover = fill the slide (crop); contain = letterbox the full image. */
  fit: 'cover' | 'contain';
}

export const DEFAULT_IMAGE_FIT: SlideImageBackground['fit'] = 'cover';

/**
 * Build the stored CSS string for an image background. A white base color sits
 * under the image so `contain` letterboxing (and transparent PNGs) reads as
 * paper, not black.
 */
export function buildImageBackground(img: SlideImageBackground): string {
  const safe = img.url.trim().replace(/"/g, '%22');
  return `#ffffff url("${safe}") center / ${img.fit} no-repeat`;
}

/** Parse the image URL + fit back out of a stored background. Null when the
 *  background isn't an image. */
export function parseImageBackground(css?: string): SlideImageBackground | null {
  if (!css || getSlideBackgroundKind(css) !== 'image') return null;
  const m = css.match(/url\((['"]?)(.*?)\1\)/i);
  if (!m || !m[2]) return null;
  return { url: m[2], fit: /\/\s*contain\b/i.test(css) ? 'contain' : 'cover' };
}

export function getImageBackground(bg?: string): SlideImageBackground {
  return parseImageBackground(bg) ?? { url: '', fit: DEFAULT_IMAGE_FIT };
}
